import { onLanguageChange, t } from '../i18n';

/**
 * The bar that says when the next round starts.
 *
 * Before this there was no answer to "how long do I have?" on screen at all: the
 * betting window is seven seconds and the only sign it was running was that the
 * bet button was enabled.
 *
 * Everything it needs was already on the wire -- `round.betting` carries
 * `bettingEndsAt` and the server's own clock -- so this is a drawing, not a new
 * source of truth. Two rules follow from that and both matter:
 *
 *   1. Time is read from the server clock (ClientCurve.serverNow), never from
 *      Date.now() directly. A wrong local clock must not move the deadline.
 *   2. Reaching zero does not start the round. It says "starting" and waits for
 *      `round.start`. The client's own countdown hitting zero while the server is
 *      a few hundred milliseconds behind would otherwise show a round that has
 *      not begun -- and, worse, teach the player to expect one.
 */
export class Countdown {
  private readonly root = must('countdown');
  private readonly label = must('countdown-label');
  private readonly fill = must('countdown-fill');

  private endsAt = 0;

  /**
   * The longest betting window seen so far, used as the bar's full width.
   *
   * A reconnect mid-window only tells us how much is left, and scaling the bar
   * to that would draw a full bar with two seconds to go. Remembering the widest
   * window makes the reconnected bar start where it honestly is. It is a display
   * scale and nothing else; the deadline is always `endsAt`.
   */
  private windowMs = 0;

  /** The last text written, so the frame loop only touches the DOM on a change. */
  private lastLabel = '';
  private lastRatio = -1;

  constructor() {
    onLanguageChange(() => {
      // Force the next frame to rewrite the label in the new language.
      this.lastLabel = '';
    });
  }

  /**
   * Shows the bar for a betting window.
   *
   * `serverNow` is the server time at the moment this was learned, which is what
   * makes the first frame correct rather than one frame late.
   */
  open(endsAtServerMs: number, serverNowMs: number): void {
    this.endsAt = endsAtServerMs;
    const remaining = endsAtServerMs - serverNowMs;
    if (remaining > this.windowMs) this.windowMs = remaining;

    this.root.removeAttribute('hidden');
    this.lastRatio = -1;
    this.lastLabel = '';
    this.frame(serverNowMs);
  }

  hide(): void {
    this.root.setAttribute('hidden', '');
    this.endsAt = 0;
  }

  /** Called once per animation frame while the betting window is open. */
  frame(serverNowMs: number): void {
    if (!this.endsAt) return;

    const remaining = Math.max(0, this.endsAt - serverNowMs);
    const total = Math.max(this.windowMs, remaining, 1);
    const ratio = Math.max(0, Math.min(1, remaining / total));

    if (Math.abs(ratio - this.lastRatio) > 0.002) {
      this.lastRatio = ratio;
      // scaleX rather than width: the browser can do this one on the compositor,
      // and this runs on every frame of every betting window.
      this.fill.style.transform = `scaleX(${ratio})`;
    }

    const text =
      remaining <= 0
        ? t('countdown.starting')
        : t('countdown.label', { seconds: (remaining / 1000).toFixed(1) });

    if (text !== this.lastLabel) {
      this.lastLabel = text;
      this.label.textContent = text;
    }
  }
}

function must(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`countdown: #${id} is missing from the page`);
  return el;
}
